import { API } from "@/lib/api";
import {
  PROJECT_DEPENDENCY_ROLE,
  ProjectDependsOn,
} from "@/schemas/commonFields";
import {
  APIService,
  FetchListRequest,
  SuccessFetchListResponse,
} from "@/services/api";
import { ReadPayroll } from "@/types/payroll";
import {
  PayrollAggregate,
  UpdatePayrollAggregate,
} from "@/types/payrollAggregate";
import {
  PayrollDepartmentBasedInspection,
  PayrollDepartmentBasedInspectionPreview,
  UpdatePayrollDepartmentBasedInspection,
} from "@/types/payrollDepartmentBasedInspection";
import {
  PayrollRoleBasedInspection,
  PayrollRoleBasedInspectionPreview,
  UpdatePayrollRoleBasedInspection,
} from "@/types/payrollRoleBasedInspection";
import { PayrollSplit, UpdatePayrollSplit } from "@/types/payrollSplit";
import type { ReadDepartmentBasedAssignments } from "@/types/departmentBasedAssignments";
import type { ReadRoleBasedAssignments } from "@/types/roleBasedAssignments";
import {
  CreateDepartmentBasedPayrollInspection,
  CreateRoleBasedPayrollInspection,
  PayrollDepartmentBasedInspectionAssignment,
} from "@/modules/payrolls/types";

const service = new APIService<ReadPayroll>(API.Payrolls);

export const PayrollsService = {
  getList: (params: FetchListRequest<ReadPayroll>) => service.getList(params),

  get: (id: string) => service.get(id),

  createPayroll: (payrollId: string, projectDependsOn: ProjectDependsOn) =>
    service.post({}, `${payrollId}/${projectDependsOn}-based/inspection`),

  splitPayroll: (payrollId: string) => service.post({}, `${payrollId}/split`),

  closePayroll: (payrollId: string) => service.post({}, `${payrollId}/close`),

  createCorrection: (payrollId: string) =>
    service.post({}, `${payrollId}/correction`),

  updatePayrollInspections: async (
    entries: (
      | UpdatePayrollDepartmentBasedInspection
      | UpdatePayrollRoleBasedInspection
    )[],
    projectDependsOn: ProjectDependsOn,
  ) => {
    const url = `${service.path}/${projectDependsOn}-based/inspection`;
    const response = await service.performFetch(url, "PATCH", entries);
    await service.handleResponseError(response);
  },

  createPayrollInspections: async (
    entry:
      | CreateDepartmentBasedPayrollInspection
      | CreateRoleBasedPayrollInspection,
    projectDependsOn: ProjectDependsOn,
  ) => {
    const url = `${service.path}/${projectDependsOn}-based/inspection`;
    const isRoleBased = projectDependsOn === PROJECT_DEPENDENCY_ROLE;
    const commonFields = {
      payroll_id: entry.payroll_id,
      store_id: entry.store_id,
      contractor_id: entry.contractor_id,
    };
    const response = await service.performFetch(
      url,
      "POST",
      isRoleBased
        ? {
            ...commonFields,
            project_role_id: (entry as CreateRoleBasedPayrollInspection)
              .project_role_id,
          }
        : {
            ...commonFields,
            department_id: (entry as CreateDepartmentBasedPayrollInspection)
              .department_id,
          },
    );

    await service.handleResponseError(response);
  },

  updatePayrollInspection: async (
    entryId: string,
    entry:
      | UpdatePayrollDepartmentBasedInspection
      | UpdatePayrollRoleBasedInspection,
    projectDependsOn: ProjectDependsOn,
  ) => {
    const url = `${service.path}/${projectDependsOn}-based/inspection/${entryId}`;
    const response = await service.performFetch(url, "PATCH", entry);
    await service.handleResponseError(response);
  },

  updatePayrollAggregates: async (entries: UpdatePayrollAggregate[]) => {
    const url = `${service.path}/aggregation`;
    const response = await service.performFetch(url, "PATCH", entries);
    await service.handleResponseError(response);
  },

  updatePayrollSplits: async (entries: UpdatePayrollSplit[]) => {
    const url = `${service.path}/split`;
    const response = await service.performFetch(url, "PATCH", entries);
    await service.handleResponseError(response);
  },

  getInspectionList: <T extends ProjectDependsOn | undefined>(
    payrollId: string,
    projectDependsOn: T,
    fetchParams?: FetchListRequest<
      T extends "department"
        ? PayrollDepartmentBasedInspection
        : PayrollRoleBasedInspection
    >,
  ): Promise<
    SuccessFetchListResponse<
      T extends "department"
        ? PayrollDepartmentBasedInspection
        : PayrollRoleBasedInspection
    >
  > => {
    return service.getList(
      fetchParams,
      `/${payrollId}/${projectDependsOn}-based/inspection`,
    ) as Promise<
      SuccessFetchListResponse<
        T extends "department"
          ? PayrollDepartmentBasedInspection
          : PayrollRoleBasedInspection
      >
    >;
  },

  getInspection: <T extends ProjectDependsOn | undefined>(
    entryId: string,
    projectDependsOn: T,
  ): Promise<
    T extends "department"
      ? PayrollDepartmentBasedInspection
      : PayrollRoleBasedInspection
  > => {
    return service.get(
      `${projectDependsOn}-based/inspection/${entryId}`,
    ) as Promise<
      T extends "department"
        ? PayrollDepartmentBasedInspection
        : PayrollRoleBasedInspection
    >;
  },

  getPreviewList: <T extends ProjectDependsOn | undefined>(
    payrollId: string,
    projectDependsOn: T,
    fetchParams?: FetchListRequest<
      T extends "department"
        ? PayrollDepartmentBasedInspectionPreview
        : PayrollRoleBasedInspectionPreview
    >,
  ): Promise<
    SuccessFetchListResponse<
      T extends "department"
        ? PayrollDepartmentBasedInspectionPreview
        : PayrollRoleBasedInspectionPreview
    >
  > => {
    return service.getList(
      fetchParams,
      `/${payrollId}/${projectDependsOn}-based/preview`,
    ) as Promise<
      SuccessFetchListResponse<
        T extends "department"
          ? PayrollDepartmentBasedInspectionPreview
          : PayrollRoleBasedInspectionPreview
      >
    >;
  },

  getAggregationList: (
    payrollId: string,
    fetchParams?: FetchListRequest<PayrollAggregate>,
  ): Promise<SuccessFetchListResponse<PayrollAggregate>> =>
    service.getList(fetchParams, `/${payrollId}/aggregation`),

  getSplitList: (
    payrollId: string,
    fetchParams?: FetchListRequest<PayrollSplit>,
  ): Promise<SuccessFetchListResponse<PayrollSplit>> =>
    service.getList(fetchParams, `/${payrollId}/split`),

  getAggregation: (entryId: string): Promise<PayrollAggregate> =>
    service.get(`aggregation/${entryId}`),

  updateAggregation: async (entryId: string, entry: UpdatePayrollAggregate) => {
    const url = `${service.path}/aggregation/${entryId}`;
    const response = await service.performFetch(url, "PATCH", entry);
    await service.handleResponseError(response);
  },

  getSplit: (entryId: string): Promise<PayrollSplit> =>
    service.get(`split/${entryId}`),

  updateSplit: async (entryId: string, entry: UpdatePayrollSplit) => {
    const url = `${service.path}/split/${entryId}`;
    const response = await service.performFetch(url, "PATCH", entry);
    await service.handleResponseError(response);
  },

  getAssignments: <T extends ProjectDependsOn | undefined>(
    payrollId: string,
    projectDependsOn: T,
    fetchParams?: FetchListRequest<
      T extends "department"
        ? ReadDepartmentBasedAssignments
        : ReadRoleBasedAssignments
    >,
  ): Promise<
    SuccessFetchListResponse<PayrollDepartmentBasedInspectionAssignment>
  > => {
    return service.getList(
      fetchParams,
      `/${payrollId}/${projectDependsOn}-based/assignments`,
    );
  },
};
